import { useState } from "react";
import {
  buildTripPlannerData,
  primeGuideContent,
  type TripPlannerData,
} from "@/lib/publicGuide";
import { saveSiteSetting } from "@/lib/adminSettings";
import { btnPrimary } from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";
import {
  AddRowButton,
  LabeledInput,
  LabeledSelect,
  RowCard,
  SectionCard,
} from "./EditorKit";
import PresetCard from "./PresetCard";

type PoolItem = TripPlannerData["pool"][number];
type Filter = TripPlannerData["filters"][number];
type Preset = TripPlannerData["presets"][number];

const TYPE_OPTIONS = [
  { value: "experience", label: "Experience" },
  { value: "farm", label: "Farm" },
  { value: "culture", label: "Konaseema" },
];

export default function TripPlannerEditor({ initial }: { initial: unknown }) {
  const [planner, setPlanner] = useState<TripPlannerData>(() => buildTripPlannerData(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { notify, node } = useAdminToast();

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await saveSiteSetting("trip_planner", planner);
      primeGuideContent({ tripPlanner: planner });
      notify("Trip planner updated.");
    } catch {
      setError("We couldn't save the planner. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const update = <K extends keyof TripPlannerData>(key: K, value: TripPlannerData[K]) =>
    setPlanner((state) => ({ ...state, [key]: value }));

  const updatePool = (index: number, patch: Partial<PoolItem>) =>
    update("pool", planner.pool.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
        <p className="text-sm text-foreground-600">
          <i className="ri-information-line"></i> The itinerary pool visitors drag into their plan, plus the ready-made presets.
        </p>
        <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Save planner
        </button>
      </div>

      {error ? (
        <p className="flex items-center gap-2 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3">
          <i className="ri-error-warning-line"></i> {error}
        </p>
      ) : null}

      {/* Pool */}
      <SectionCard
        icon="ri-layout-grid-line"
        title="Itinerary Pool"
        subtitle="Every experience, farm and activity a visitor can add to their day-by-day plan."
      >
        <div className="space-y-3">
          {planner.pool.map((item, index) => (
            <RowCard
              key={index}
              title={item.title || `Item ${index + 1}`}
              onRemove={() => update("pool", planner.pool.filter((_, i) => i !== index))}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput label="ID" value={item.id} onChange={(value) => updatePool(index, { id: value })} />
                <LabeledSelect
                  label="Type"
                  value={item.type}
                  onChange={(value) => updatePool(index, { type: value as PoolItem["type"] })}
                  options={TYPE_OPTIONS}
                />
                <LabeledInput label="Icon" value={item.icon} onChange={(value) => updatePool(index, { icon: value })} placeholder="ri-plant-line" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledInput label="Title" value={item.title} onChange={(value) => updatePool(index, { title: value })} />
                <LabeledInput label="Meta" value={item.meta} onChange={(value) => updatePool(index, { meta: value })} placeholder="Full day · Konaseema" />
                <LabeledInput label="Duration" value={item.duration} onChange={(value) => updatePool(index, { duration: value })} placeholder="8 hrs" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <LabeledInput label="Link" value={item.href} onChange={(value) => updatePool(index, { href: value })} placeholder="/experiences/…" />
                <LabeledInput
                  label="Map place"
                  value={item.mapQuery}
                  onChange={(value) => updatePool(index, { mapQuery: value })}
                  placeholder="Konaseema, Andhra Pradesh, India"
                />
              </div>
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() =>
            update("pool", [
              ...planner.pool,
              {
                id: "",
                type: "experience",
                title: "",
                meta: "",
                icon: "ri-plant-line",
                duration: "",
                href: "/",
                mapQuery: "Konaseema, East Godavari, Andhra Pradesh, India",
              } as PoolItem,
            ])
          }
          label="Add pool item"
        />
      </SectionCard>

      {/* Filters */}
      <SectionCard icon="ri-filter-3-line" title="Pool Filters" subtitle="The filter chips above the itinerary pool.">
        <div className="space-y-3">
          {planner.filters.map((filter, index) => (
            <RowCard
              key={index}
              onRemove={() => update("filters", planner.filters.filter((_, i) => i !== index))}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <LabeledInput
                  label="ID"
                  value={filter.id}
                  onChange={(value) => update("filters", planner.filters.map((f, i) => (i === index ? { ...f, id: value } : f)))}
                />
                <LabeledInput
                  label="Label"
                  value={filter.label}
                  onChange={(value) => update("filters", planner.filters.map((f, i) => (i === index ? { ...f, label: value } : f)))}
                />
              </div>
            </RowCard>
          ))}
        </div>
        <AddRowButton
          onClick={() => update("filters", [...planner.filters, { id: "", label: "" } as Filter])}
          label="Add filter"
        />
      </SectionCard>

      {/* Presets */}
      <SectionCard
        icon="ri-route-line"
        title="Ready-Made Presets"
        subtitle="The one-tap itineraries shown at the top of the planner."
      >
        <div className="space-y-4">
          {planner.presets.map((preset, index) => (
            <PresetCard
              key={index}
              preset={preset}
              onChange={(next) =>
                update("presets", planner.presets.map((p, i) => (i === index ? next : p)))
              }
              onRemove={() => update("presets", planner.presets.filter((_, i) => i !== index))}
            />
          ))}
        </div>
        <AddRowButton
          onClick={() =>
            update("presets", [
              ...planner.presets,
              {
                id: "",
                title: "",
                duration: "",
                audience: "",
                summary: "",
                image: "",
                days: [{ label: "Day 1", theme: "", stops: [] }],
              } as Preset,
            ])
          }
          label="Add preset"
        />
      </SectionCard>

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          Save planner
        </button>
      </div>

      {node}
    </div>
  );
}